import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import polarAceWolf from '../../assets/images/polar_ace_wolf_dark.png';

function ChatRail({ chats, activeChat, onSelect, onJoin }) {
  const allChat = chats.find((c) => c.type === 'all');
  const userChats = chats.filter((c) => c.type === 'user');
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinUsername, setJoinUsername] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (joinOpen) {
      inputRef.current?.focus();
    }
  }, [joinOpen]);

  const isActive = (chat) => {
    if (chat.type === 'all') return activeChat.type === 'all';
    return activeChat.type === 'user' && activeChat.owner === chat.owner;
  };

  const handleJoinSubmit = (event) => {
    event.preventDefault();
    const normalizedUsername = joinUsername.trim().replace(/^@+/, '');
    if (!normalizedUsername || !onJoin) return;
    onJoin(normalizedUsername);
    setJoinUsername('');
    setJoinOpen(false);
  };

  const handleJoinClose = () => {
    setJoinUsername('');
    setJoinOpen(false);
  };

  return (
    <aside className="community-rail" aria-label="Community chats">
      <div className="community-rail__list">
        {allChat && (
          <button
            type="button"
            className={`community-rail__item community-rail__item--all ${
              isActive(allChat) ? 'community-rail__item--active' : ''
            }`}
            onClick={() => onSelect(allChat)}
            aria-label="All Chat"
            aria-current={isActive(allChat) ? 'page' : undefined}
            title="All Chat"
          >
            <WolfLogo />
          </button>
        )}

        {userChats.map((chat) => (
          <UserChatButton
            key={chat.id}
            chat={chat}
            active={isActive(chat)}
            onSelect={onSelect}
          />
        ))}

        {onJoin && (
          joinOpen ? (
            <form className="community-rail__search" onSubmit={handleJoinSubmit}>
              <input
                ref={inputRef}
                type="text"
                className="community-rail__search-input"
                placeholder="@username"
                aria-label="Join creator chat by username"
                value={joinUsername}
                onChange={(event) => setJoinUsername(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    handleJoinClose();
                  }
                }}
              />
              <button
                type="button"
                className="community-rail__search-close"
                onClick={handleJoinClose}
                aria-label="Close join creator chat form"
              >
                ×
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="community-rail__item community-rail__item--search"
              onClick={() => setJoinOpen(true)}
              aria-label="Join creator chat"
              title="Join creator chat"
            >
              <PlusIcon />
            </button>
          )
        )}
      </div>
    </aside>
  );
}

function UserChatButton({ chat, active, onSelect }) {
  const owner = chat.owner || '';
  const label = `@${owner}'s chat`;
  const initial = owner.charAt(0).toUpperCase() || '?';

  return (
    <button
      type="button"
      className={`community-rail__item ${active ? 'community-rail__item--active' : ''}`}
      onClick={() => onSelect(chat)}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      title={label}
    >
      {chat.ownerAvatar ? (
        <img
          className="community-rail__avatar"
          src={chat.ownerAvatar}
          alt=""
        />
      ) : (
        <span className="community-rail__avatar community-rail__avatar--fallback">
          {initial}
        </span>
      )}
    </button>
  );
}

function WolfLogo() {
  return (
    <img
      src={polarAceWolf}
      alt="Polar Ace"
      className="community-rail__wolf-logo"
    />
  );
}

function PlusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

ChatRail.propTypes = {
  chats: PropTypes.array.isRequired,
  activeChat: PropTypes.shape({
    type: PropTypes.oneOf(['all', 'user']).isRequired,
    owner: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onJoin: PropTypes.func,
};

export default ChatRail;
